import { useCallback, useContext, useMemo, useState } from "react";
import { Event, NotificationType } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import { Icon } from "@iconify/react";
import LabelButton from "../Buttons/LabelButton";
import {
  GetDateWithTime,
  handlePluralString,
  MARKITAI,
} from "@markit/common.utils";
import ThreeDotsButton from "../Buttons/ThreeDotsButton";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { deleteEventData, eventActions } from "../../redux/slices/eventSlice";
import { useDispatch, useSelector } from "react-redux";
import { MixpanelContext } from "../../context/AnalyticsService";
import { getAccountState } from "../../redux/slices/accountSlice";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { CopyableLinkDisplay } from "../Links/CopyCustomLinkify";
import { updateEventManualSoldOut } from "../../utils/dashboardUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { useTheme } from "../../hooks/useTheme";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { getTotalLinkViews } from "../../utils/trackingUtils";
import ShareEventModal from "../EventDashboard/DashboardModals/ShareEventModal";

export const FORM_ITEM_FLEX_SECTIONS = [2, 1, 1, 6];

type GenericTrackingLinkItemProps = {
  item: Event;
  setExistingTrackingLink: (link: Event) => void;
};

export const GenericTrackingLinkItem = ({
  item,
  setExistingTrackingLink,
}: GenericTrackingLinkItemProps) => {
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const { uid } = useSelector(getAccountState).account.accountData;
  const mixpanel = useContext(MixpanelContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [numOpens, setNumOpens] = useState(0);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [shareModal, setShareModal] = useState(false);

  useAsyncOnMount(async () => {
    const numOpens = await getTotalLinkViews(uid, item.id);
    setNumOpens(numOpens);
    setLoadingAnalytics(false);
  });

  // TODO: Currently unused. Maybe add back and update the modal to explain that it will affect mass texts that use the tracking link
  const deleteGenericTrackingLinkOnPress = useCallback(() => {
    dispatch(deleteEventData(item, uid, mixpanel));
    showNotificationBanner(
      dispatch,
      "Tracking Link Deleted",
      NotificationType.NEGATIVE,
      "ion:trash"
    );
  }, [dispatch, item, mixpanel, uid]);

  const switchTrackingLinkStatus = useCallback(async () => {
    await updateEventManualSoldOut(item);
    dispatch(
      eventActions.updateEvent({
        ...item,
        isManualSoldOut: !item.isManualSoldOut,
      })
    );
    showNotificationBanner(
      dispatch,
      item.isManualSoldOut
        ? "Tracking Link Activated"
        : "Tracking Link Deactivated",
      item.isManualSoldOut
        ? NotificationType.AFFIRMATIVE
        : NotificationType.NEGATIVE,
      item.isManualSoldOut ? "ion:checkmark-circle" : "ion:close-circle"
    );
  }, [dispatch, item]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [
      {
        title: "Edit",
        icon: "ion:create",
        key: "edit",
        onPress: () => setExistingTrackingLink(item),
      },
      {
        title: "Share",
        icon: "ion:share",
        key: "share",
        onPress: () => setShareModal(true),
      },
      {
        title: item.isManualSoldOut ? "Activate" : "Deactivate",
        icon: item.isManualSoldOut
          ? "ion:checkmark-circle"
          : "ion:close-circle",
        key: "deactivate",
        onPress: switchTrackingLinkStatus,
      },
      // {
      //   title: "Delete",
      //   icon: "ion:trash",
      //   key: "delete",
      //   onPress: () => setShowDeleteModal(true),
      //   isDestructive: true,
      // },
    ];

    return items;
  }, [item, setExistingTrackingLink, switchTrackingLinkStatus]);

  return (
    <>
      <div className="AlignedRow" style={{ height: 45 }}>
        <div
          className="AlignedRowSelect"
          onClick={() => setExistingTrackingLink(item)}
          style={{ flex: FORM_ITEM_FLEX_SECTIONS[0], gap: 20 }}
        >
          <div className="ColumnNormal" style={{ gap: 7, width: 240 }}>
            <span className="bodyMedium OneLineText">{item.title}</span>
            <span className="smallBodySubtext TextOverflowEllipsis">
              Created: {GetDateWithTime(item.createdAt, true)}
            </span>
          </div>
        </div>
        <div style={{ flex: FORM_ITEM_FLEX_SECTIONS[1] }}>
          <LabelButton
            color={Colors.BLACK}
            backgroundColor={
              item.isManualSoldOut
                ? Colors.ORANGE1 + "1a"
                : Colors.GREEN1 + "1a"
            }
            text={item.isManualSoldOut ? "Inactive" : "Active"}
            icon={
              !item.isManualSoldOut ? (
                <Icon
                  icon={"ion:ellipse"}
                  height={8}
                  style={{ color: Colors.GREEN1 }}
                />
              ) : undefined
            }
            containerStyles={{
              border: `1px solid ${
                item.isManualSoldOut ? Colors.ORANGE1 : Colors.GREEN1
              }`,
              marginLeft: 14,
            }}
          />
        </div>
        <div style={{ flex: FORM_ITEM_FLEX_SECTIONS[2] }}>
          <StandardBorderedContainer
            containerStyles={{
              borderRadius: 8,
              paddingInline: 9,
              paddingBlock: 7,
              backgroundColor: Colors.WHITE1,
              width: "fit-content",
              marginLeft: 14,
              border: `1px solid ${theme.SubText.color}`,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 2 }}>
              <span style={{ fontSize: 14, color: theme.PrimaryText.color }}>
                {loadingAnalytics ? "--" : numOpens}{" "}
                <span style={{ ...theme.SubText, fontSize: 12 }}>
                  {handlePluralString("Click", numOpens)}
                </span>
              </span>
            </div>
          </StandardBorderedContainer>
        </div>
        <div
          className="AlignedRowSpaced"
          style={{
            flex: FORM_ITEM_FLEX_SECTIONS[3],
            justifyContent: "flex-end",
            gap: 24,
          }}
        >
          <CopyableLinkDisplay
            copyText={`${MARKITAI}/e/${item.id}`}
            containerStyles={{
              backgroundColor: Colors.WHITE1,
              display: "flex",
              padding: 14,
              border: `0.5px solid ${Colors.BLACK}`,
              borderRadius: 100,
              width: 390,
            }}
            altTextColor={Colors.BLACK}
          />
          <DropdownMenu
            dropdownItems={dropdownItems}
            trigger={["click"]}
            placement="bottomLeft"
            altWidth={150}
          >
            <ThreeDotsButton onPress={() => {}} />
          </DropdownMenu>
        </div>
      </div>
      {showDeleteModal ? (
        <ConfirmDeleteModal
          heading="Delete Tracking Link?"
          subtext="Are you sure you want to delete this tracking link? This cannot be undone."
          deleteButtonText="Delete Tracking Link"
          icon={
            <Icon
              icon={"ion:trash"}
              height={35}
              style={{ color: Colors.RED3 }}
            />
          }
          hideModal={!showDeleteModal}
          setIsVisible={setShowDeleteModal}
          deleteOnPress={deleteGenericTrackingLinkOnPress}
        />
      ) : null}
      {shareModal ? (
        <ShareEventModal
          event={item}
          revShare={false}
          hideTrackingLinks
          closeShareModal={() => setShareModal(false)}
          isTrackingLinksView={false}
          setIsTrackingLinksView={() => {}}
          trackingLinkData={[]}
          fetchMoreTrackingLinks={() => {}}
          canLoadMoreTrackingLinks={false}
          setTrackingLinkModalVisible={() => {}}
        />
      ) : null}
    </>
  );
};
